import { Injectable } from '@angular/core';
import { BaseApiService } from '@core/services/base-api.service';
import type {
  DeliveryTrackerDocument,
  DeliveryTrackerResultDelivery,
} from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker-document.types';
import type { PaginatedResponseDto } from '@kerberos-compliance/compliance-shared-ts/common/dtos/response.paginated.dtos';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import { NamedRoleMap } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { KnownContentTypeMap } from '@kerberos-compliance/compliance-shared-ts/core/storage/enums/known-content-type.enum';
import type { ListRequestQueryParameters } from '@kerberos-compliance/compliance-shared-ui';
import { auditJustificationContext } from '@kerberos-compliance/compliance-shared-ui';
import { buildPaginationSortParameters } from '@shared/utils/pagination-sort-parameters.utility';

const deliveryTrackerDocumentEndpoints = {
  documents: (deliveryTrackerId: UUID): string => `delivery-tracker/details/${deliveryTrackerId}/document`,
  document: (deliveryTrackerId: UUID, documentId: UUID): string =>
    `delivery-tracker/details/${deliveryTrackerId}/document/${documentId}`,
} as const;

const ResultDeliveryFormFields = {
  files: 'files',
  knownContentTypes: 'knownContentTypes',
  closeTrackerAndNotify: 'closeTrackerAndNotify',
} as const;

@Injectable()
export class DeliveryTrackerDocumentService extends BaseApiService {
  private readonly featureRole = NamedRoleMap.InternalUser;

  /**
   * Lists the documents linked to one delivery tracker - both what the customer uploaded and what Kerberos
   * delivered back. Access is decided server-side from the caller's user type and grants.
   *
   * @param {UUID} deliveryTrackerId - The tracker whose documents are requested.
   * @param {ListRequestQueryParameters} queryData - Pagination and sorting.
   * @returns {Promise<PaginatedResponseDto<DeliveryTrackerDocument> | undefined>} The paginated documents.
   */
  public async searchDeliveryTrackerDocuments(
    deliveryTrackerId: UUID,
    queryData: ListRequestQueryParameters,
  ): Promise<PaginatedResponseDto<DeliveryTrackerDocument> | undefined> {
    return this.get<PaginatedResponseDto<DeliveryTrackerDocument>, Record<string, string>>(
      deliveryTrackerDocumentEndpoints.documents(deliveryTrackerId),
      {
        ...this.getRequestOptions(this.featureRole),
        params: buildPaginationSortParameters(queryData),
      },
    );
  }

  /**
   * Delivers documents to an open delivery tracker in a single request: every file is stored with the document
   * type chosen for it and linked to the tracker, and - only when asked for - the tracker is closed and the
   * customer notified. The API virus-scans inline, so an infected file fails the whole request and nothing is
   * stored, the tracker stays untouched and no notification goes out.
   *
   * @param {UUID} deliveryTrackerId - The open tracker receiving the documents.
   * @param {DeliveryTrackerResultDelivery} delivery - The staged files with their document types and the
   * close-and-notify decision.
   * @returns {Promise<void>} Resolves once the API accepted the delivery.
   */
  public async deliverResult(deliveryTrackerId: UUID, delivery: DeliveryTrackerResultDelivery): Promise<void> {
    const formData = new FormData();
    for (const stagedFile of delivery.files) {
      formData.append(ResultDeliveryFormFields.files, stagedFile.file, stagedFile.file.name);
      formData.append(
        ResultDeliveryFormFields.knownContentTypes,
        String(stagedFile.knownContentType ?? KnownContentTypeMap.Unknown),
      );
    }
    formData.append(ResultDeliveryFormFields.closeTrackerAndNotify, String(delivery.closeTrackerAndNotify));

    await this.postMultipart<void>(
      deliveryTrackerDocumentEndpoints.documents(deliveryTrackerId),
      formData,
      this.featureRole,
    );
  }

  /**
   * Soft-deletes one result document of a tracker. The mandatory justification travels in the request context,
   * which `auditMetadataInterceptor` turns into the audit metadata envelope, so the reason is recorded on the
   * server-side audit entry. The API rejects customer uploads and the last risk analysis of a completed tracker.
   *
   * @param {UUID} deliveryTrackerId - The tracker the document belongs to.
   * @param {UUID} documentId - Any version id of the result document.
   * @param {string} justification - The mandatory reason the document is being removed.
   * @returns {Promise<void>} Resolves once the document is soft-deleted.
   */
  public async deleteDocument(deliveryTrackerId: UUID, documentId: UUID, justification: string): Promise<void> {
    await this.delete<void>(deliveryTrackerDocumentEndpoints.document(deliveryTrackerId, documentId), {
      ...this.getRequestOptions(this.featureRole),
      context: auditJustificationContext(justification),
    });
  }
}
